// Helper utilities for group settings modal data shaping.
export const resolveGroupDeviceIds = (group) =>
  Array.isArray(group?.devices) ? group.devices : [];

export const resolveGroupDevices = (connectedDevices, group) => {
  if (!Array.isArray(connectedDevices) || !group) return [];
  const idSet = new Set(resolveGroupDeviceIds(group));
  return connectedDevices.filter((device) => idSet.has(device.id));
};

export const getGroupDeviceTypeInfo = (devices) => {
  const typeSet = new Set(devices.map((device) => device?.device_type).filter(Boolean));
  const types = [...typeSet];
  return {
    types,
    isUniform: typeSet.size <= 1,
    deviceType: typeSet.size === 1 ? types[0] : null,
  };
};

/**
 * Newest rows fetched for each device in one page of history. Shared by every
 * history surface — the group modal, the group settings panel, the group widget
 * and the single-device history card and modal — so one read means the same
 * thing everywhere; `loadMore` walks back a page at a time from here.
 */
export const GROUP_HISTORY_PAGE_SIZE = 100;

/**
 * The history window starts at the epoch. A command device (siren, flashing
 * light, road closure) writes a row only when a person commands it, so a
 * bounded window is legitimately empty for an idle one — and the page load
 * holds only its newest row. "The newest rows, whatever their age" is what the
 * history table shows; `GROUP_HISTORY_PAGE_SIZE` bounds it, which makes this a
 * bounded newest-N read rather than a walk through all of history.
 */
export const GROUP_HISTORY_START_MS = 0;

/**
 * History rows for the group modal, flattened newest-first from the devices'
 * live data. Each row is stamped with its owning device's node_id: data points
 * carry `uid`/`did` but not `node_id`, so the history's Device ID column was
 * blank without it. Stage devices are excluded (their points are sensor
 * readings, not events) unless `includeStage` — the per-device history view
 * shows whatever the selected device has.
 */
export const buildGroupHistoryRows = (devices, { includeStage = false } = {}) =>
  (Array.isArray(devices) ? devices : [])
    .filter((device) => includeStage || device?.device_type !== 'Stage')
    .flatMap((device) =>
      (Array.isArray(device?.data) ? device.data : []).map((point) => ({
        ...point,
        node_id: point?.node_id ?? device?.node_id,
      })),
    )
    .sort((nextEntry, prevEntry) => (prevEntry?.ts ?? 0) - (nextEntry?.ts ?? 0));

export const buildVirtualDeviceSeed = (groupDevices, groupType, macroEligible) => {
  if (!macroEligible || groupDevices.length === 0) return null;
  return {
    ...groupDevices[0],
    device_type: groupType,
    data: buildGroupHistoryRows(groupDevices),
  };
};

const SIREN_DEVICE_TYPE = 'Technology Bridge_Siren/Giant Voice';

export const isSirenDevice = (device) => device?.device_type === SIREN_DEVICE_TYPE;

export const audioLabel = (audio) =>
  audio?.displayName || audio?.name || (audio?.id != null ? `audio ${audio.id}` : 'audio');

/** The four fields the backend reads; nothing else travels (`device_ids` is the server's to check). */
export const audioForGroupCommand = (audio) => {
  if (!audio || typeof audio !== 'object') return null;
  return {
    id: audio.id ?? null,
    relay_id: audio.relay_id ?? null,
    name: audio.name ?? null,
    displayName: audio.displayName ?? audio.name ?? null,
  };
};

/**
 * What ONE siren can be given, from the catalogue: files on every siren
 * (`device_ids` empty), files uploaded to this siren, and — since the backend
 * associates a chosen file automatically — every other file, which will be
 * added to this siren when the command is saved.
 */
export const sirenAudioOptionsFor = (device, allAudio) => {
  const files = Array.isArray(allAudio) ? allAudio.filter(Boolean) : [];
  const universal = files.filter((f) => !Array.isArray(f.device_ids) || f.device_ids.length === 0);
  const own = files.filter(
    (f) => Array.isArray(f.device_ids) && f.device_ids.length > 0 && f.device_ids.some((id) => `${id}` === `${device?.id}`),
  );
  const elsewhere = files.filter((f) => !universal.includes(f) && !own.includes(f));
  return { universal, own, elsewhere };
};

/**
 * The body extras for `POST /device-group/:id/command`: interval, repeat,
 * volume as before, plus — for sirens — the group's audio file and one
 * `targets` entry per siren that plays its own (`perDeviceAudio` is
 * `{ [deviceId]: audio | null }`, null meaning "same as group"). Audio is sent
 * only for an alerting command: idle plays nothing, and the backend records
 * a file only when one is played.
 */
export const buildGroupCommandArgs = (deviceState, perDeviceAudio = null) => {
  const commandArgs = {};
  const intervalValue = Number(deviceState?.interval);
  if (Number.isFinite(intervalValue)) commandArgs.interval = intervalValue;
  const repeatValue = Number(deviceState?.repeat);
  if (Number.isFinite(repeatValue)) commandArgs.repeat = repeatValue;
  const volumeValue = Number(deviceState?.volume);
  if (Number.isFinite(volumeValue)) commandArgs.volume = volumeValue;

  const alerting = Number(deviceState?.status) > 0;
  if (!alerting) return commandArgs;

  const groupAudio = audioForGroupCommand(deviceState?.audio);
  if (groupAudio) commandArgs.audio = groupAudio;

  const targets = Object.entries(perDeviceAudio || {})
    .filter(([, audio]) => audio)
    .map(([deviceId, audio]) => ({ device_id: Number(deviceId), audio: audioForGroupCommand(audio) }))
    .filter((t) => Number.isInteger(t.device_id) && t.device_id > 0);
  if (targets.length > 0) commandArgs.targets = targets;
  return commandArgs;
};
